import { useEffect } from "react";
import { useThree } from "@react-three/fiber";
import { useRef, useState } from "react";
import { Camera, Intersection, Plane, Raycaster, Vector2, Vector2Like, Vector3 } from "three";
import { SelectedItem } from "@/3d/SelectedItem";

const DRAG_START_THRESHOLD = 5;

const plane = new Plane(new Vector3(0, 1, 0), 0);

export const useMouseInteractions = ({
    cameraRef,
    onDrag,
    onDragStart,
    onDragEnd,
    onClick,
    filter,
    isEnabled = true,
}: {
    cameraRef: React.RefObject<Camera>;
    onDrag: (item: SelectedItem, target: Vector2Like) => void;
    onDragStart: (item: SelectedItem) => boolean;
    onDragEnd: () => void;
    onClick?: (item: SelectedItem | undefined) => void;
    filter?: (item: SelectedItem) => boolean;
    isEnabled?: boolean;
}) => {
    const raycasterRef = useRef(new Raycaster());

    const [currentTarget, setCurrentTarget] = useState<SelectedItem | undefined>(undefined);
    const [mouseDownPosition, setMouseDownPosition] = useState<Vector2Like | undefined>(undefined);

    const [isDragging, setIsDragging] = useState(false);

    const { scene } = useThree();

    useEffect(() => {
        const handleMouseDown = (event: MouseEvent) => {
            if (!(event.target instanceof HTMLCanvasElement) || event.button !== 0) {
                return;
            }
            setMouseDownPosition({ x: event.clientX, y: event.clientY });
            if (!isEnabled) return;

            const camera = cameraRef.current;
            const raycaster = raycasterRef.current;

            if (!camera || !raycaster) return;

            const { x, y } = getNormalizedMousePosition(event);

            raycaster.setFromCamera(new Vector2(x, y), camera);

            let intersects: Intersection[] = [];
            try {
                intersects = raycaster.intersectObjects(scene.children, true);
            } catch (error) {
                console.error(error);
                return;
            }

            if (intersects.length > 0) {
                const firstIntersect = intersects
                    .map((intersect) => intersect.object.userData)
                    .filter((data): data is SelectedItem => "selectedItemType" in data)
                    .find(filter ?? (() => true));

                if (!firstIntersect) return;
                setCurrentTarget(firstIntersect);
            }
        };

        window.addEventListener("mousedown", handleMouseDown);

        return () => {
            window.removeEventListener("mousedown", handleMouseDown);
        };
    }, [scene, onDragStart, filter, isEnabled]);

    useEffect(() => {
        const handleMouseUp = (event: MouseEvent) => {
            if (!isEnabled) return;
            if (
                !isDragging &&
                mouseDownPosition !== undefined &&
                !checkIsDragging(mouseDownPosition, event)
            ) {
                onClick?.(currentTarget);
            }

            setMouseDownPosition(undefined);

            if (currentTarget) {
                setCurrentTarget(undefined);
                setIsDragging(false);
                onDragEnd();
            }
        };

        window.addEventListener("mouseup", handleMouseUp);

        return () => {
            window.removeEventListener("mouseup", handleMouseUp);
        };
    }, [currentTarget, onDragEnd, onClick, isDragging, mouseDownPosition, isEnabled]);

    useEffect(() => {
        const handleMouseMove = (event: MouseEvent) => {
            if (!isEnabled) return;
            if (!currentTarget || event.buttons === 0) return;

            if (!isDragging) {
                if (checkIsDragging(mouseDownPosition, event)) {
                    const isDragStarted = onDragStart(currentTarget);
                    setIsDragging(isDragStarted);
                    if (!isDragStarted) {
                        return;
                    }
                } else {
                    return;
                }
            }

            const camera = cameraRef.current;
            const raycaster = raycasterRef.current;
            if (!camera || !raycaster) return;

            const { x, y } = getNormalizedMousePosition(event);

            raycaster.setFromCamera(new Vector2(x, y), camera);

            const intersectionPoint = new Vector3();
            const point = raycaster.ray.intersectPlane(plane, intersectionPoint);

            if (point) {
                onDrag(currentTarget, new Vector2(intersectionPoint.x, intersectionPoint.z));
            }
        };

        window.addEventListener("mousemove", handleMouseMove);

        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
        };
    }, [currentTarget, onDrag, isDragging, isEnabled]);
};

const checkIsDragging = (mouseDownPosition: Vector2Like | undefined, event: MouseEvent) => {
    if (!mouseDownPosition) return false;

    const deltaX = event.clientX - mouseDownPosition.x;
    const deltaY = event.clientY - mouseDownPosition.y;
    return Math.abs(deltaX) > DRAG_START_THRESHOLD || Math.abs(deltaY) > DRAG_START_THRESHOLD;
};

const getNormalizedMousePosition = (event: MouseEvent) => {
    const canvas = document.querySelector("canvas");
    if (!canvas) return { x: event.clientX, y: event.clientY };

    // Get canvas bounds
    const rect = canvas.getBoundingClientRect();

    // Calculate NDC coordinates (-1 to +1)
    const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    return { x, y };
};
